import React from 'react';
import { createTheme, useTheme } from '@mui/material/styles';
import { grey, blueGrey } from '@mui/material/colors';
import { deepmerge } from '@mui/utils';
import { THEMES } from 'src/constants';
import ckEditorDarkThemeCss from 'src/styles/ckeditor-dark-theme-css';
import { lightShadows, darkShadows } from './shadows';
import typography from './typography';

const baseOptions = {
  components: {
    MuiAvatar: {
      styleOverrides: {
        fallback: {
          height: '75%',
          width: '75%'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none'
        }
      }
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h6'
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: 'hidden'
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          marginRight: '16px'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: '12px'
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        }
      }
    }
  },
  typography
};

const themesOptions = {
  [THEMES.LIGHT]: {
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            '&::placeholder': {
              opacity: 0.86,
              color: '#42526e'
            }
          }
        }
      }
    },
    palette: {
      mode: 'light',
      action: {
        active: '#6b778c'
      },
      background: {
        paper: '#ffffff',
        sidebar: '#ffffff',
        default: blueGrey[50],
        menuItemActive: grey[200],
        menuItemHover: grey[100]
      },
      error: {
        contrastText: '#ffffff',
        main: '#f44336'
      },
      primary: {
        main: '#1890ff'
      },
      success: {
        contrastText: '#ffffff',
        main: '#4caf50'
      },
      text: {
        primary: '#172b4d',
        secondary: '#6b778c',
        menuItemActive: '#212121'
      },
      warning: {
        contrastText: '#ffffff',
        main: '#ff9800'
      }
    },
    shadows: lightShadows
  },
  [THEMES.DARK]: {
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: '1px solid rgba(145, 158, 171, 0.24)'
          }
        }
      }
    },
    palette: {
      mode: 'dark',
      background: {
        default: '#141c22',
        paper: '#23303d',
        sidebar: '#1c262f',
        menuItemActive: '#2b333b',
        menuItemHover: '#2b333b'
      },
      divider: 'rgba(145, 158, 171, 0.24)',
      error: {
        contrastText: '#ffffff',
        main: '#f44336'
      },
      primary: {
        main: '#1890ff'
      },
      success: {
        contrastText: '#ffffff',
        main: '#4caf50'
      },
      text: {
        primary: '#ffffff',
        secondary: '#919eab',
        menuItemActive: '#ffffff'
      },
      warning: {
        contrastText: '#ffffff',
        main: '#ff9800'
      }
    },
    shadows: darkShadows
  }
};

export const CKEditorDarkTheme = () => {
  const theme = useTheme();
  return (
    theme.palette.mode === 'dark' && (
      <style id="ck-editor-dark-theme">{ckEditorDarkThemeCss}</style>
    )
  );
};

export const createMuiTheme = (config = {}, overrides = {}) => {
  let themeOptions = themesOptions[config.theme];

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`));
    themeOptions = themesOptions[THEMES.LIGHT];
  }

  const theme = createTheme({
    ...deepmerge(baseOptions, themeOptions),
    overrides
  });

  return theme;
};
